import { call, put, select, all } from 'redux-saga/effects';
import * as types from '../../constants/ActionTypes';
import Api from '../../functions/Api';
import { smallGuid } from '../../functions/id/guid';
import { API_METHODS } from './publishingConstants';

/**
 * Add, remove or edit tickets
 * @param {Object} data - ticket data
 * @param {String} purpose - which type of API to use (`"add"`, `"edit"` or `"remove"`)
 * @param {Number} idAttraction - attraction id
 * @param {Number} idTicket - ticket id
 */
export function* sendTicketsData({ data, purpose, idAttraction, idTicket }) {
  const method = API_METHODS[purpose];

  try {
    let response = {};
    if (method === 'post')
      response = yield call(Api[method], `v2/tickets/`, {
        data: {
          ...data,
          attractions: [idAttraction],
        },
        isDecamelize: false,
      });
    else
      response = yield call(Api[method], `v2/tickets/${idTicket}/`, {
        data: {
          ...data,
        },
        isDecamelize: false,
      });

    if (purpose === 'add' && response.data && response.data.id) {
      yield put({
        type: types.SEND_TICKETS_DATA_SUCCESS,
        ticketId: response.data.id,
        attractionId: idAttraction,
        data: response.data,
      });

      yield put({
        type: types.SEND_TICKETS_CATEGORIES_DATA,
        purpose: 'add',
        data: { label: data.pricingLabel },
        idTicket: response.data.id,
      });
    }

    if (purpose === 'edit' && response.data && response.data.id) {
      yield put({
        type: types.SEND_TICKETS_DATA_SUCCESS,
        ticketId: response.data.id,
        attractionId: idAttraction,
        data: response.data,
      });
    }

    if (purpose === 'remove' && response) {
      yield put({
        type: types.DELETE_TICKETS_DATA_SUCCESS,
        ticketId: idTicket,
      });
    }
  } catch (error) {
    yield put({
      type: types.SEND_TICKETS_DATA_FAILURE,
      errors: [error],
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

export function* sendTicketCategoriesData({
  data,
  purpose,
  idTicket,
  idCategory = null,
}) {
  const method = API_METHODS[purpose];
  const pricingCategoryId = smallGuid();

  try {
    let response = {};
    if (method === 'post')
      response = yield call(Api[method], `v2/tickets/${idTicket}/categories/`, {
        data: {
          ...data,
          pricingCategoryId,
        },
        isDecamelize: false,
      });
    else
      response = yield call(
        Api[method],
        `v2/tickets/${idTicket}/categories/${idCategory}/`,
        {
          data: {
            ...data,
          },
          isDecamelize: false,
        }
      );

    if (purpose === 'add' && response.data && response.data.id) {
      yield put({
        type: types.SEND_TICKETS_CATEGORIES_DATA_SUCCESS,
        ticketId: idTicket,
        data: response.data,
      });
    }

    if (purpose === 'edit' && response.data && response.data.id) {
      yield put({
        type: types.SEND_TICKETS_CATEGORIES_DATA_SUCCESS,
        ticketId: idTicket,
        data: response.data,
      });
    }

    if (purpose === 'remove' && response) {
      yield put({
        type: types.DELETE_TICKETS_CATEGORIES_DATA_SUCCESS,
        idTicket,
        idCategory,
      });
    }
  } catch (error) {
    yield put({
      type: types.SEND_TICKETS_CATEGORIES_DATA_FAILURE,
      errors: [error],
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

/**
 * Removing tickets categories and tickets from attraction
 * @param {Number} idAttraction - attraction id
 * @param {Number} idTicket - ticket id
 * @param {boolean} withAttraction - ticket id
 */
export function* deleteTicket({ idAttraction, idTicket, withAttraction }) {
  try {
    const attractions = yield select((state) => state.publishing.attractions);
    yield all(
      attractions
        .find((a) => a.id === idAttraction)
        .tickets.find((t) => t.id === idTicket)
        .pricingCategories.map((pc) => pc.id)
        .map((value) =>
          call(sendTicketCategoriesData, {
            type: types.SEND_TICKETS_CATEGORIES_DATA,
            data: {},
            purpose: 'remove',
            idTicket,
            idCategory: value,
          })
        )
    );

    const attractionsAfter = yield select(
      (state) => state.publishing.attractions
    );

    if (
      attractionsAfter
        .find((a) => a.id === idAttraction)
        .tickets.find((t) => t.id === idTicket).pricingCategories.length === 0
    )
      yield call(sendTicketsData, {
        type: types.SEND_TICKETS_DATA,
        data: {},
        purpose: 'remove',
        idAttraction,
        idTicket,
      });

    const attractionsCleared = yield select(
      (state) => state.publishing.attractions
    );

    if (
      withAttraction &&
      attractionsCleared.find((a) => a.id === idAttraction).tickets.length === 0
    )
      yield put({
        type: types.SEND_ATTRACTIONS_DATA,
        data: { id: idAttraction },
        purpose: 'remove',
      });
  } catch (error) {
    yield put({
      type: types.SEND_TICKETS_CATEGORIES_DATA_FAILURE,
      errors: [error],
    });
    yield put({
      type: types.SEND_TICKETS_DATA_FAILURE,
      errors: [error],
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}
